import React, { useState, useEffect } from "react";

/*
  Usage:
    import FilterSidebar from "../../components/FilterSidebar";

    <div style={{ display: "flex", alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0, padding: "24px 28px" }}>
        {your page content}
      </div>
      <FilterSidebar />
    </div>
*/

const BASE_URL = "https://mis.empowerplus.in";

/* Static fallback options (used when API hasn't loaded yet or fails) */
export const FILTER_DEFS = [
    {
        key: "fy",
        label: "FY",
        type: "multi",
        options: ["2024–25", "2023–24", "2022–23", "2021–22"],
    },
    {
        key: "date",
        label: "Date",
        type: "daterange",
    },
    {
        key: "fundingSource",
        label: "Funding Source",
        type: "multi",
        options: ["CSR Funded", "Govt. Funded", "Foreign Funded", "Self-Funded"],
    },
    {
        key: "scheme",
        label: "Scheme",
        type: "hierarchical",
        options: [{ label: "Scheme", children: ["PMKVY", "NAPS", "DDUGKY", "State Scheme"] }],
    },
    {
        key: "programmeArea",
        label: "subSchemes",
        type: "hierarchical",
        options: [{ label: "Sub Scheme", children: ["Youth Skilling", "Upskilling", "EDP", "Community Development", "Preventive Health", "Vocational Education"] }],
    },
    {
        key: "project",
        label: "Project",
        type: "hierarchical",
        options: [{ label: "Project", children: ["Project Alpha", "Project Beta", "Project Gamma", "Project Delta"] }],
    },
    {
        key: "phase",
        label: "Phase",
        type: "multi",
        options: ["Phase I", "Phase II", "Phase III"],
    },
    {
        key: "state",
        label: "State",
        type: "hierarchical",
        options: [{ label: "State", children: ["Maharashtra", "Gujarat", "Rajasthan", "Uttar Pradesh", "Madhya Pradesh", "Karnataka", "Bihar", "Odisha"] }],
    },
    {
        key: "district",
        label: "District",
        type: "hierarchical",
        options: [{ label: "District", children: ["Pune", "Nashik", "Ahmedabad", "Surat", "Jaipur", "Lucknow", "Patna"] }],
    },
    {
        key: "centre",
        label: "Centre",
        type: "hierarchical",
        options: [{ label: "Centre", children: ["EP/TC-A00001", "EP/TC-A00002", "EP/TC-A00003", "EP/TC-A00004"] }],
    },
    {
        key: "funder",
        label: "Funder",
        type: "multi",
        options: ["Tata Foundation", "Azim Premji Foundation", "HCL Foundation", "HDFC CSR"],
    },
    /* sector & gender — always static */
    {
        key: "sector",
        label: "Sector",
        type: "hierarchical",
        options: [{ label: "Sector", children: ["Manufacturing", "Construction", "Retail", "Healthcare", "IT / ITES", "Agriculture"] }],
    },
    {
        key: "gender",
        label: "Gender",
        type: "multi",
        options: ["Male", "Female", "Transgender"],
    },
];

/* API response key → FILTER_DEFS key */
const API_KEY_MAP = {
    fy:            "fy",
    fundingSource: "fundingSource",
    schemes:       "scheme",
    subSchemes:    "programmeArea",
    projects:      "project",
    phases:        "phase",
    states:        "state",
    districts:     "district",
    centers:       "centre",
    funders:       "funder",
};

const DEFAULT_OPEN = { fy: true, programmeArea: true, state: true };

function FilterGroup({ filter, open, onToggle }) {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate]     = useState("");

    return (
        <div className="flt-group">
            <button className="flt-group-hd" onClick={onToggle}>
                <span className="flt-group-lbl">{filter.label}</span>
                <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
            </button>

            {open && (
                <div className="flt-body">
                    {filter.type === "daterange" && (
                        <div className="flt-daterange">
                            <label>Start</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="flt-date-input" />
                            <label>End</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="flt-date-input" />
                        </div>
                    )}

                    {filter.type === "multi" && filter.options.map(opt => (
                        <label key={opt} className="flt-check-row">
                            <input type="checkbox" className="flt-cb" readOnly />
                            <span>{opt}</span>
                        </label>
                    ))}

                    {filter.type === "hierarchical" && filter.options.map(group => (
                        <div key={group.label} className="flt-hier-group">
                            {group.children.map(child => (
                                <label key={child} className="flt-check-row">
                                    <input type="checkbox" className="flt-cb" readOnly />
                                    <span>{child}</span>
                                </label>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function FilterSidebar({ filters = FILTER_DEFS, defaultOpen = DEFAULT_OPEN }) {
    const [openFilters, setOpenFilters] = useState(defaultOpen);
    const [apiOptions, setApiOptions]   = useState(null); /* null = not yet fetched */

    useEffect(() => {
        const token = localStorage.getItem("token");
        fetch(`${BASE_URL}/api/view-dashboard/filter-options`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setApiOptions(data); })
            .catch(() => { /* silently keep static fallback */ });
    }, []);

    /* Merge API options into filter defs; sector & gender always stay static */
    const resolvedFilters = filters.map(f => {
        if (!apiOptions) return f;
        const apiKey = API_KEY_MAP[f.key];
        if (!apiKey || !Array.isArray(apiOptions[apiKey])) return f;
        const vals = apiOptions[apiKey];
        if (f.type === "multi") return { ...f, options: vals };
        if (f.type === "hierarchical") {
            return { ...f, options: [{ label: f.options[0]?.label || f.label, children: vals }] };
        }
        return f;
    });

    const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
    const reset  = () => setOpenFilters(defaultOpen);

    return (
        <aside className="flt-sidebar">
            <div className="flt-header">
                <span className="flt-title">Filters</span>
                <button className="flt-reset" onClick={reset}>Reset All</button>
            </div>

            <div className="flt-note">
                All filter views change according to selection.
            </div>

            {resolvedFilters.map(filter => (
                <FilterGroup
                    key={filter.key}
                    filter={filter}
                    open={!!openFilters[filter.key]}
                    onToggle={() => toggle(filter.key)}
                />
            ))}

            <button className="flt-apply">Apply Filters</button>

            <style>{`
.flt-sidebar {
    width: 256px;
    flex-shrink: 0;
    background: #fff;
    border-left: 1px solid #e5e7eb;
    padding: 16px 14px 24px;
    min-height: 100vh;
    position: sticky;
    top: 0;
    max-height: 100vh;
    overflow-y: auto;
    font-family: Inter, system-ui, sans-serif;
}
.flt-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 10px;
}
.flt-title { font-size: 14px; font-weight: 900; color: #0f172a; }
.flt-reset {
    font-size: 11px; font-weight: 700; color: #6b7280;
    background: none; border: 1px solid #e5e7eb;
    border-radius: 6px; padding: 3px 8px; cursor: pointer;
}
.flt-reset:hover { color: #ef4444; border-color: #fca5a5; }
.flt-note {
    font-size: 10px; color: #9ca3af; line-height: 1.4;
    background: #f8fafc; border-radius: 6px;
    padding: 6px 8px; margin-bottom: 12px;
}
.flt-group { border-bottom: 1px solid #f1f5f9; }
.flt-group-hd {
    display: flex; align-items: center; justify-content: space-between;
    width: 100%; background: none; border: none;
    padding: 9px 2px; cursor: pointer;
    font-size: 12px; font-weight: 800; color: #374151;
    text-align: left;
}
.flt-group-hd:hover { color: #2563eb; }
.flt-group-lbl { flex: 1; }
.flt-chevron {
    font-size: 14px; color: #9ca3af;
    transform: rotate(90deg); display: inline-block;
    transition: transform 0.2s; line-height: 1;
}
.flt-chevron.open { transform: rotate(270deg); }
.flt-body { padding: 4px 0 10px 4px; display: flex; flex-direction: column; gap: 4px; }
.flt-check-row {
    display: flex; align-items: center; gap: 7px;
    font-size: 12px; color: #4b5563; cursor: pointer;
    padding: 2px 4px; border-radius: 5px;
}
.flt-check-row:hover { background: #eff6ff; color: #2563eb; }
.flt-check-child { padding-left: 20px; }
.flt-cb { accent-color: #2563eb; width: 13px; height: 13px; flex-shrink: 0; cursor: pointer; }
.flt-hier-group { margin-bottom: 4px; }
.flt-hier-parent {
    display: flex; align-items: center; gap: 7px;
    font-size: 11px; font-weight: 800; color: #6b7280;
    padding: 3px 4px; text-transform: uppercase; letter-spacing: 0.5px;
}
.flt-hier-parent-lbl { font-size: 10px; }
.flt-daterange {
    display: flex; flex-direction: column; gap: 5px;
    font-size: 11px; font-weight: 700; color: #6b7280;
}
.flt-date-input {
    border: 1px solid #e5e7eb; border-radius: 6px;
    padding: 5px 7px; font-size: 11px; color: #374151; outline: none;
}
.flt-date-input:focus { border-color: #2563eb; }
.flt-apply {
    width: 100%; margin-top: 16px;
    background: #2563eb; color: #fff;
    border: none; border-radius: 10px;
    padding: 10px; font-size: 13px; font-weight: 800; cursor: pointer;
}
.flt-apply:hover { background: #1d4ed8; }
            `}</style>
        </aside>
    );
}
